import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { CheckCircle, Warning } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';

export const CARD_STATUS = {
  ERROR: 'error',
  PROGRESS: 'progress',
  SUCCESS: 'success',
};

const CardStatusOverlay = ({ type = null }) => {
  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.75)',
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        fontSize: `128px`,
        justifyContent: 'center',
        left: 0,
        margin: 1,
        opacity: type ? 1 : 0,
        padding: 0,
        pointerEvents: type ? 'auto' : 'none',
        position: 'absolute',
        right: 0,
        top: 0,
        transition: 'opacity 0.5s ease',
        zIndex: 1,
      }}
    >
      <Box className="material-icons" sx={{ mb: '10%' }}>
        {type === CARD_STATUS.ERROR && (
          <Warning
            sx={{
              color: '_next.error.main',
              fontSize: '124px',
            }}
          />
        )}
        {type === CARD_STATUS.SUCCESS && (
          <CheckCircle
            sx={{
              color: '_next.primary.main',
              fontSize: '124px',
            }}
          />
        )}
        {type === CARD_STATUS.PROGRESS && <CircularProgress size={32} />}
      </Box>
    </Box>
  );
};

CardStatusOverlay.propTypes = {
  type: PropTypes.oneOf([CARD_STATUS.ERROR, CARD_STATUS.PROGRESS, CARD_STATUS.SUCCESS]),
};

CardStatusOverlay.defaultProps = {
  type: null,
};

const CustomizableCard = ({
  cardHeader,
  cardFooter,
  cardHeaderRightSideContent,
  cardContent,
  status,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, position: 'relative' }}>
      <Card sx={{ borderRadius: '4px', width: '430px' }}>
        <CardStatusOverlay type={status} />
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            height: '64px',
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          <Tooltip arrow title={cardHeader}>
            <Typography
              color="text.primary"
              component="div"
              sx={{
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                verticalAlign: 'bottom',
                whiteSpace: 'nowrap',
              }}
              variant="h6"
            >
              {cardHeader}
            </Typography>
          </Tooltip>
          <Box aria-describedby="card-header-right-side-content">{cardHeaderRightSideContent}</Box>
        </Box>
        <CardContent sx={{ p: 0 }}>{cardContent}</CardContent>
        <CardActions
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            justifyContent: 'flex-end',
            p: 2,
          }}
        >
          <Box
            sx={{
              alignItems: { sm: 'center', xs: 'stretch' },
              display: 'flex',
              flexDirection: { sm: 'row-reverse', xs: 'column' },
              justifyContent: { sm: 'space-between', xs: 'center' },
            }}
            width="100%"
          >
            {cardFooter}
          </Box>
        </CardActions>
      </Card>
    </Box>
  );
};

export default CustomizableCard;

CustomizableCard.propTypes = {
  cardContent: PropTypes.node.isRequired,
  cardFooter: PropTypes.node.isRequired,
  cardHeader: PropTypes.node.isRequired,
  cardHeaderRightSideContent: PropTypes.node,
  status: PropTypes.oneOf([CARD_STATUS.ERROR, CARD_STATUS.PROGRESS, CARD_STATUS.SUCCESS, null]),
};

CustomizableCard.defaultProps = {
  cardHeaderRightSideContent: null,
  status: null,
};
